import { MEASURE_NOTE, metricsFor, rankFor, VECTORS } from '../components/simConstants'

const VECTOR_BY_API = {
  link: 'link',
  url: 'link',
  dataEntry: 'dataEntry',
  data_entry: 'dataEntry',
  attachment: 'attachmentHtml',
}

const EMPTY_COUNTS = { submitted: 0, clicked: 0, reported: 0, opened: 0, none: 0 }

export const EMPTY_SIMULATION_OVERVIEW = {
  available: false,
  vector: VECTORS.dataEntry,
  counts: EMPTY_COUNTS,
  recovered: 0,
  delivered: 0,
  scheduled: 0,
  notDelivered: 0,
  opened: 0,
  metrics: {
    lured: 0,
    reportedTotal: 0,
    clickRate: 0,
    reportRate: 0,
    resilience: null,
    recovered: 0,
    remediationRate: null,
  },
  rank: 'notRated',
  rankReason: null,
  measureNote: null,
  enrolled: {
    total: 0,
    completed: 0,
    inProgress: 0,
    late: 0,
    notStarted: 0,
    overdue: 0,
  },
  trainingModule: null,
  phase: null,
  speed: { firstReportMin: null, fastest: [] },
  sendWindow: null,
}

const attachmentVectorKey = (format) => {
  if (!format) {
    return 'attachmentHtml'
  }

  const normalized = String(format).toUpperCase()
  const byFormat = {
    HTML: 'attachmentHtml',
    PDF: 'attachmentPdf',
    DOCX: 'attachmentDocx',
    XLS: 'attachmentXls',
    XLSX: 'attachmentXls',
  }

  return byFormat[normalized] || 'attachmentHtml'
}

const resolveVector = (payload) => {
  if (payload.fileFormat || payload.vector === 'attachment') {
    return VECTORS[attachmentVectorKey(payload.fileFormat)]
  }

  const key = VECTOR_BY_API[payload.vector] || 'dataEntry'
  return VECTORS[key]
}

const resolvePhase = (payload) => {
  if (
    payload.phase === 'sending' ||
    payload.phase === 'collecting' ||
    payload.phase === 'completed'
  ) {
    return payload.phase
  }

  return 'collecting'
}

export const adaptSimulationOverview = (payload) => {
  if (payload == null || payload.available === false) {
    return EMPTY_SIMULATION_OVERVIEW
  }

  const vector = resolveVector(payload)
  const phase = resolvePhase(payload)
  const counts = { ...EMPTY_COUNTS, ...payload.counts }
  const delivered = payload.delivered ?? 0
  const opened = payload.opened ?? 0
  const recovered = payload.recovered ?? 0
  const enrolledIn = payload.enrolled || { completed: 0, inProgress: 0, late: 0 }
  const metrics = metricsFor({
    counts,
    recovered,
    opened,
    enrolled: { total: 0, completed: enrolledIn.completed || 0 },
  })

  const closed = phase === 'completed'
  const completed = Math.min(enrolledIn.completed || 0, metrics.lured)
  const inProgress = Math.min(enrolledIn.inProgress || 0, Math.max(0, metrics.lured - completed))
  const open = Math.max(0, metrics.lured - completed - inProgress)
  const enrolled = {
    total: metrics.lured,
    completed,
    inProgress,
    late: Math.min(enrolledIn.late || 0, completed),
    notStarted: closed ? 0 : open,
    overdue: closed ? open : 0,
  }

  const rank = rankFor({
    phase,
    opened,
    delivered,
    clickRate: metrics.clickRate,
    measure: vector.measure,
  })

  const rankReason =
    vector.measure === 'none'
      ? 'unmeasurable'
      : delivered === 0
        ? 'noneDelivered'
        : delivered < 10
          ? 'small'
          : phase === 'sending'
            ? 'sending'
            : rank === 'notRated'
              ? 'tooFew'
              : null

  const firstReportMin = payload.firstReportMin ?? null

  return {
    available: true,
    vector,
    counts,
    recovered,
    delivered,
    scheduled: payload.scheduled ?? 0,
    notDelivered: payload.notDelivered ?? 0,
    opened,
    metrics,
    rank,
    rankReason,
    measureNote: MEASURE_NOTE[vector.measure],
    enrolled,
    trainingModule: payload.trainingModuleAttached ? { title: 'training' } : null,
    phase,
    speed: {
      firstReportMin,
      fastest: firstReportMin == null ? [] : [{ minutes: firstReportMin }],
    },
    sendWindow: payload.sendWindow
      ? {
          days: payload.sendWindow.days,
          sentDays: payload.sendWindow.sentDays,
          perDay: payload.sendWindow.perDay,
          start: payload.sendWindow.start,
          end: payload.sendWindow.end,
        }
      : null,
  }
}
